import React, { useState } from "react";
import { Button, Input } from "reactstrap";
import DataTable, { TableColumn } from "react-data-table-component";
import { MenuItem } from "../../../../../../redux/menuSlice";
import { DEFAULT_IMAGE_URL } from "../../../../../../constants/constans";
import "./index.css"
import { getImageUrlWithFallback } from "../../../../../../utility/Utility";

interface OrderItemsTableProps {
    orderItems: MenuItem[];
    handleAddItem: (item: MenuItem) => void;
    handleRemoveItem: (item: MenuItem) => void;
    setOrderItems: any;
    handleDiscountChange: (item: MenuItem, discount: number) => void;
    unavailableItemIds?: Set<string>;
}

const OrderItemsTable: React.FC<OrderItemsTableProps> = ({
    orderItems,
    handleAddItem,
    handleRemoveItem,
    handleDiscountChange,
    unavailableItemIds = new Set(),
}) => {
    const [editingDiscount, setEditingDiscount] = useState<{ [key: string]: string }>({});

    const handleDiscountInputChange = (item: MenuItem, value: string) => {
        const itemKey = item.id || item.menuItemId || '';
        setEditingDiscount(prev => ({ ...prev, [itemKey]: value }));
    };

    const handleDiscountBlur = (item: MenuItem) => {
        const itemKey = item.id || item.menuItemId || '';
        const discountValue = parseFloat(editingDiscount[itemKey] || '0');
        if (!isNaN(discountValue) && discountValue >= 0) {
            handleDiscountChange(item, discountValue);
        }
    };

    const isUnavailable = (item: MenuItem): boolean => {
        const itemId = item.id || item.menuItemId || '';
        return unavailableItemIds.has(itemId);
    };

    const orderItemsColumns: TableColumn<MenuItem>[] = [
        {
            name: <h6 className="fw-bold">Image</h6>,
            selector: (row) => row?.imageUrl,
            cell: (row: { imageUrl: any; displayName: string | undefined }) => {
                const effectiveImageUrl = Array.isArray(row.imageUrl) && row.imageUrl.length > 0
                    ? row.imageUrl[0]
                    : row.imageUrl || DEFAULT_IMAGE_URL;
                return (
                    <img
                        src={getImageUrlWithFallback(effectiveImageUrl, DEFAULT_IMAGE_URL, 64)}
                        alt={row.imageUrl || "Menu Item"}
                        className="menuitem-image"
                        onError={(e) => (e.currentTarget.src = DEFAULT_IMAGE_URL)}
                    />
                );
            },
            sortable: false,
        },
        {
            name: <h6 className="fw-bold">Name</h6>,
            selector: (row) => row?.displayName,
            sortable: true,
            width: '30%',
            cell: (row: MenuItem) => (
                <div>
                    <span>{row?.displayName}</span>
                    {isUnavailable(row) && (
                        <span
                            className="ms-2 badge bg-warning text-dark"
                            title="This item is not available on the selected date"
                            style={{ fontSize: 11, verticalAlign: 'middle' }}
                        >
                            ⚠ Unavailable
                        </span>
                    )}
                </div>
            ),
        },
        {
            name: <h6 className="fw-bold">Portions Size</h6>,
            selector: (row) => row?.portionLabel,
            sortable: true,
        },
        {
            name: <h6 className="fw-bold">(Rs.) Unit Price</h6>,
            cell: (row) => {
                let unitPrice = row?.calculatedPrice ?? 0;
                if (row.itemType === 'CustomMenu' && row.customItemTotal !== undefined) {
                    unitPrice = row.customItemTotal;
                }
                return <div>Rs. {unitPrice.toFixed(2)}</div>;
            },
        },
        {
            name: <h6 className="fw-bold">Quantity</h6>,
            sortable: true,
            center: true,
            cell: (row: MenuItem) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Button
                        size="sm"
                        color="light"
                        className="me-2"
                        onClick={() => handleRemoveItem && handleRemoveItem(row)}
                        disabled={!(row?.quantity && row.quantity > 0)}
                    >
                        -
                    </Button>
                    <span style={{ minWidth: 36, textAlign: 'center', display: 'inline-block' }}>
                        {row?.quantity || 0}
                    </span>
                    <Button
                        size="sm"
                        color="light"
                        className="ms-2"
                        onClick={() => handleAddItem && handleAddItem(row)}
                    >
                        +
                    </Button>
                </div>
            ),
        },
        {
            name: <h6 className="fw-bold">(Rs.)Discount Per Pack</h6>,
            center: true,
            cell: (row: MenuItem) => {
                const itemKey = row.id || row.menuItemId || '';
                const currentDiscount = editingDiscount[itemKey] !== undefined
                    ? editingDiscount[itemKey]
                    : (row?.perItemManualDiscount?.toString() || '0');
                return (
                    <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={currentDiscount}
                        onChange={(e) => handleDiscountInputChange(row, e.target.value)}
                        onBlur={() => handleDiscountBlur(row)}
                        style={{ width: '100px', textAlign: 'center' }}
                        placeholder="0.00"
                    />
                );
            },
        },
        {
            name: <h6 className="fw-bold">(Rs.) Price</h6>,
            right: true,
            cell: (row) => {
                let itemPrice = row?.calculatedPrice ?? 0;
                const quantity = row?.quantity ?? 0;
                const discount = row?.perItemManualDiscount ?? 0;
                if (row.itemType === 'CustomMenu' && row.customItemTotal !== undefined) {
                    itemPrice = row.customItemTotal;
                }
                const totalBeforeDiscount = itemPrice * quantity;
                const totalDiscount = discount * quantity;
                const finalPrice = totalBeforeDiscount - totalDiscount;
                return <div>Rs. {finalPrice.toFixed(2)}</div>;
            }
        },
    ];

    // Custom row styling — yellow background for unavailable items
    const conditionalRowStyles = [
        {
            when: (row: MenuItem) => isUnavailable(row),
            style: {
                backgroundColor: '#fff3cd',
                borderLeft: '4px solid #ffc107',
            },
        },
    ];

    return (
        <>
            <DataTable
                columns={orderItemsColumns}
                data={orderItems}
                pagination
                responsive
                highlightOnHover
                conditionalRowStyles={conditionalRowStyles}
            />
        </>
    );
};

export default OrderItemsTable;